
import { useState, useEffect } from 'react';
import { Button } from '../../components/atoms/Button';
import { Sidebar } from '../../components/feature/Sidebar';
import { TopBar } from '../../components/feature/TopBar';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import * as XLSX from 'xlsx';

interface ReportData {
  period: string;
  production: number;
  consumption: number;
  gridExport: number;
  gridImport: number;
  selfConsumption: number;
  savings: number;
}

interface MonthlyReport {
  month: string;
  totalProduction: number;
  totalConsumption: number;
  totalSavings: number;
  avgEfficiency: number;
  co2Avoided: number;
}

export default function ReportsPage() {
  const [selectedPeriod, setSelectedPeriod] = useState('month');
  const [selectedFormat, setSelectedFormat] = useState('pdf');
  const [selectedType, setSelectedType] = useState('executive'); // executive or technical
  const [isGenerating, setIsGenerating] = useState(false);
  const [reportData, setReportData] = useState<ReportData[]>([]);
  const [monthlyReports, setMonthlyReports] = useState<MonthlyReport[]>([]);

  useEffect(() => {
    // Simular datos de reportes
    const generateReportData = () => {
      const data: ReportData[] = [];
      const months = ['Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio'];

      months.forEach((month) => {
        data.push({
          period: month,
          production: 450 + Math.random() * 200,
          consumption: 380 + Math.random() * 150,
          gridExport: 120 + Math.random() * 80,
          gridImport: 50 + Math.random() * 40,
          selfConsumption: 75 + Math.random() * 20,
          savings: 85000 + Math.random() * 30000 // CLP
        });
      });

      setReportData(data);
    };

    const generateMonthlyReports = () => {
      const reports: MonthlyReport[] = [
        {
          month: 'Junio 2024',
          totalProduction: 542.8,
          totalConsumption: 425.6,
          totalSavings: 127500,
          avgEfficiency: 87.2,
          co2Avoided: 245.8
        },
        {
          month: 'Mayo 2024',
          totalProduction: 498.3,
          totalConsumption: 401.2,
          totalSavings: 115800,
          avgEfficiency: 84.5,
          co2Avoided: 226.1
        },
        {
          month: 'Abril 2024',
          totalProduction: 467.9,
          totalConsumption: 389.4,
          totalSavings: 108900,
          avgEfficiency: 82.1,
          co2Avoided: 212.4
        }
      ];
      setMonthlyReports(reports);
    };

    generateReportData();
    generateMonthlyReports();
  }, []);

  const handleGenerateReport = async () => {
    setIsGenerating(true);

    try {
      await new Promise(resolve => setTimeout(resolve, 1500));

      const timestamp = new Date().getTime();
      const reportTypeName = selectedType === 'executive' ? 'ejecutivo' : 'tecnico';
      const filename = `reporte-${reportTypeName}-${selectedPeriod}-${timestamp}`;

      if (selectedFormat === 'pdf') {
        if (selectedType === 'executive') {
          generateExecutivePDF(filename);
        } else {
          generateTechnicalPDF(filename);
        }
      } else {
        generateExcel(filename);
      }
    } catch (error) {
      console.error('Error al generar el reporte:', error);
    } finally {
      setIsGenerating(false);
    }
  };

  const addPDFHeader = (doc: jsPDF) => {
    // Logo y encabezado
    const pageWidth = doc.internal.pageSize.getWidth();

    // Fondo del encabezado
    doc.setFillColor(251, 146, 60); // Orange-400
    doc.rect(0, 0, pageWidth, 35, 'F');

    // Logo circular
    doc.setFillColor(255, 237, 213); // Orange-100
    doc.circle(20, 17.5, 8, 'F');
    doc.setFontSize(16);
    doc.setTextColor(251, 146, 60);
    doc.text('☀', 16.5, 21);

    // Título
    doc.setFontSize(18);
    doc.setTextColor(255, 255, 255);
    doc.setFont('helvetica', 'bold');
    doc.text('SolarF', 32, 18);

    doc.setFontSize(9);
    doc.setFont('helvetica', 'normal');
    doc.text('ENERGY MONITOR', 32, 24);

    // Fecha
    doc.setFontSize(9);
    doc.setTextColor(255, 255, 255);
    const date = new Date().toLocaleDateString('es-CL', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
    doc.text(date, pageWidth - 15, 20, { align: 'right' });
  };

  const addPDFFooter = (doc: jsPDF, pageNum: number) => {
    const pageHeight = doc.internal.pageSize.getHeight();
    const pageWidth = doc.internal.pageSize.getWidth();

    doc.setFontSize(8);
    doc.setTextColor(150, 150, 150);
    doc.text(
      `SolarF Energy Monitor | Página ${pageNum}`,
      pageWidth / 2,
      pageHeight - 10,
      { align: 'center' }
    );
  };

  const generateExecutivePDF = (filename: string) => {
    const doc = new jsPDF();

    addPDFHeader(doc);

    // Título del reporte
    doc.setFontSize(20);
    doc.setTextColor(31, 41, 55);
    doc.setFont('helvetica', 'bold');
    doc.text('Reporte Ejecutivo', 14, 50);

    doc.setFontSize(12);
    doc.setTextColor(107, 114, 128);
    doc.setFont('helvetica', 'normal');
    doc.text('Análisis de Rendimiento Energético y Retorno de Inversión', 14, 58);

    // Resumen ejecutivo
    doc.setFontSize(14);
    doc.setTextColor(31, 41, 55);
    doc.setFont('helvetica', 'bold');
    doc.text('Resumen Ejecutivo', 14, 72);

    const totalProduction = reportData.reduce((sum, r) => sum + r.production, 0);
    const totalSavings = reportData.reduce((sum, r) => sum + r.savings, 0);
    const totalCO2 = monthlyReports.reduce((sum, r) => sum + r.co2Avoided, 0);
    const avgEfficiency = monthlyReports.reduce((sum, r) => sum + r.avgEfficiency, 0) / monthlyReports.length;

    // KPIs principales
    const kpis = [
      ['Métrica', 'Valor', 'Impacto'],
      ['Energía Generada', `${totalProduction.toFixed(0)} kWh`, 'Producción total del período'],
      ['Ahorro Económico', `$${totalSavings.toLocaleString('es-CL', { maximumFractionDigits: 0 })}`, 'Reducción en costos energéticos'],
      ['CO₂ Evitado', `${totalCO2.toFixed(1)} kg`, 'Contribución ambiental'],
      ['Eficiencia Promedio', `${avgEfficiency.toFixed(1)}%`, 'Rendimiento del sistema']
    ];

    autoTable(doc, {
      startY: 78,
      head: [kpis[0]],
      body: kpis.slice(1),
      theme: 'grid',
      headStyles: {
        fillColor: [59, 130, 246],
        textColor: 255,
        fontStyle: 'bold',
        fontSize: 10
      },
      bodyStyles: { fontSize: 9 },
      columnStyles: {
        0: { fontStyle: 'bold', cellWidth: 50 },
        1: { cellWidth: 40, halign: 'right' },
        2: { cellWidth: 'auto' }
      }
    });

    // Análisis financiero
    const finalY = (doc as any).lastAutoTable.finalY || 130;
    doc.setFontSize(14);
    doc.setTextColor(31, 41, 55);
    doc.setFont('helvetica', 'bold');
    doc.text('Análisis Financiero', 14, finalY + 15);

    doc.setFontSize(10);
    doc.setTextColor(75, 85, 99);
    doc.setFont('helvetica', 'normal');

    const roi = ((totalSavings / 5000000) * 100).toFixed(1); // Asumiendo inversión de 5M CLP
    const paybackMonths = (5000000 / (totalSavings / 6)).toFixed(0);

    doc.text(`• Retorno de Inversión (ROI): ${roi}% del período analizado`, 14, finalY + 25);
    doc.text(`• Período de Recuperación Estimado: ${paybackMonths} meses`, 14, finalY + 32);
    doc.text(`• Ahorro Mensual Promedio: $${(totalSavings / 6).toLocaleString('es-CL', { maximumFractionDigits: 0 })}`, 14, finalY + 39);
    doc.text(`• Proyección Anual: $${(totalSavings * 2).toLocaleString('es-CL', { maximumFractionDigits: 0 })}`, 14, finalY + 46);

    // Recomendaciones
    doc.setFontSize(14);
    doc.setTextColor(31, 41, 55);
    doc.setFont('helvetica', 'bold');
    doc.text('Recomendaciones Estratégicas', 14, finalY + 60);

    doc.setFontSize(10);
    doc.setTextColor(75, 85, 99);
    doc.setFont('helvetica', 'normal');

    doc.text('1. Optimizar consumo en horas de mayor producción solar', 14, finalY + 70);
    doc.text('2. Considerar expansión del sistema para maximizar ROI', 14, finalY + 77);
    doc.text('3. Implementar almacenamiento para aumentar autoconsumo', 14, finalY + 84);

    addPDFFooter(doc, 1);

    doc.save(`${filename}.pdf`);
  };

  const generateTechnicalPDF = (filename: string) => {
    const doc = new jsPDF();

    addPDFHeader(doc);

    // Título
    doc.setFontSize(20);
    doc.setTextColor(31, 41, 55);
    doc.setFont('helvetica', 'bold');
    doc.text('Reporte Técnico', 14, 50);

    doc.setFontSize(12);
    doc.setTextColor(107, 114, 128);
    doc.setFont('helvetica', 'normal');
    doc.text('Análisis Detallado de Producción y Consumo Energético', 14, 58);

    // Tabla de datos técnicos
    const tableColumn = ["Período", "Producción\n(kWh)", "Consumo\n(kWh)", "Export.\n(kWh)", "Import.\n(kWh)", "Autocons.\n(%)", "Ahorro\n(CLP)"];
    const tableRows: any[] = [];

    reportData.forEach(row => {
      const data = [
        row.period,
        row.production.toFixed(1),
        row.consumption.toFixed(1),
        row.gridExport.toFixed(1),
        row.gridImport.toFixed(1),
        row.selfConsumption.toFixed(1),
        `$${row.savings.toLocaleString('es-CL', { maximumFractionDigits: 0 })}`
      ];
      tableRows.push(data);
    });

    autoTable(doc, {
      head: [tableColumn],
      body: tableRows,
      startY: 68,
      theme: 'grid',
      styles: { fontSize: 8, cellPadding: 3 },
      headStyles: { fillColor: [59, 130, 246], textColor: 255, fontStyle: 'bold' },
      columnStyles: {
        0: { cellWidth: 25 },
        1: { cellWidth: 22, halign: 'right' },
        2: { cellWidth: 22, halign: 'right' },
        3: { cellWidth: 22, halign: 'right' },
        4: { cellWidth: 22, halign: 'right' },
        5: { cellWidth: 22, halign: 'right' },
        6: { cellWidth: 30, halign: 'right' }
      }
    });

    // Estadísticas técnicas
    const finalY = (doc as any).lastAutoTable.finalY || 150;
    doc.setFontSize(14);
    doc.setTextColor(31, 41, 55);
    doc.setFont('helvetica', 'bold');
    doc.text('Estadísticas del Sistema', 14, finalY + 15);

    const totalProduction = reportData.reduce((sum, r) => sum + r.production, 0);
    const totalConsumption = reportData.reduce((sum, r) => sum + r.consumption, 0);
    const totalExport = reportData.reduce((sum, r) => sum + r.gridExport, 0);
    const totalImport = reportData.reduce((sum, r) => sum + r.gridImport, 0);

    const stats = [
      ['Métrica', 'Valor Total', 'Promedio'],
      ['Producción Solar', `${totalProduction.toFixed(0)} kWh`, `${(totalProduction / 6).toFixed(1)} kWh/mes`],
      ['Consumo Total', `${totalConsumption.toFixed(0)} kWh`, `${(totalConsumption / 6).toFixed(1)} kWh/mes`],
      ['Exportación a Red', `${totalExport.toFixed(0)} kWh`, `${(totalExport / 6).toFixed(1)} kWh/mes`],
      ['Importación de Red', `${totalImport.toFixed(0)} kWh`, `${(totalImport / 6).toFixed(1)} kWh/mes`]
    ];

    autoTable(doc, {
      startY: finalY + 20,
      head: [stats[0]],
      body: stats.slice(1),
      theme: 'striped',
      headStyles: { fillColor: [59, 130, 246], textColor: 255 },
      bodyStyles: { fontSize: 9 }
    });

    addPDFFooter(doc, 1);

    doc.save(`${filename}.pdf`);
  };

  const generateExcel = (filename: string) => {
    const workbook = XLSX.utils.book_new();

    if (selectedType === 'executive') {
      // Hoja de resumen ejecutivo
      const summaryData = [
        ['REPORTE EJECUTIVO - SOLARF ENERGY MONITOR'],
        [`Generado: ${new Date().toLocaleDateString('es-CL')}`],
        [],
        ['MÉTRICAS CLAVE'],
        ['Métrica', 'Valor', 'Impacto'],
        ['Energía Generada', `${reportData.reduce((sum, r) => sum + r.production, 0).toFixed(0)} kWh`, 'Producción total'],
        ['Ahorro Económico', `$${reportData.reduce((sum, r) => sum + r.savings, 0).toLocaleString('es-CL')}`, 'Reducción de costos'],
        ['CO₂ Evitado', `${monthlyReports.reduce((sum, r) => sum + r.co2Avoided, 0).toFixed(1)} kg`, 'Impacto ambiental'],
        [],
        ['ANÁLISIS FINANCIERO'],
        ['Concepto', 'Valor'],
        ['Ahorro Mensual Promedio', `$${(reportData.reduce((sum, r) => sum + r.savings, 0) / 6).toLocaleString('es-CL')}`],
        ['Proyección Anual', `$${(reportData.reduce((sum, r) => sum + r.savings, 0) * 2).toLocaleString('es-CL')}`]
      ];

      const ws1 = XLSX.utils.aoa_to_sheet(summaryData);
      XLSX.utils.book_append_sheet(workbook, ws1, "Resumen Ejecutivo");
    }

    // Hoja de datos detallados
    const detailedData = reportData.map(row => ({
      'Período': row.period,
      'Producción (kWh)': row.production.toFixed(1),
      'Consumo (kWh)': row.consumption.toFixed(1),
      'Exportación (kWh)': row.gridExport.toFixed(1),
      'Importación (kWh)': row.gridImport.toFixed(1),
      'Autoconsumo (%)': row.selfConsumption.toFixed(1),
      'Ahorro (CLP)': row.savings.toFixed(0)
    }));

    const ws2 = XLSX.utils.json_to_sheet(detailedData);
    XLSX.utils.book_append_sheet(workbook, ws2, "Datos Detallados");

    XLSX.writeFile(workbook, `${filename}.xlsx`);
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <Sidebar />
      <div className="flex-1 ml-[var(--sidebar-width)]">
        <TopBar title="Reportes y Descargas" />
        <div className="p-6 space-y-8">

          {/* Hero Section */}
          <div className="relative overflow-hidden rounded-3xl bg-gradient-to-r from-blue-900 to-indigo-900 shadow-xl p-8">
            <div className="absolute inset-0 opacity-20">
              <div className="absolute inset-0 bg-[url('https://images.unsplash.com/photo-1454165804606-c3d57bc86b40?ixlib=rb-4.0.3&auto=format&fit=crop&w=2000&q=80')] bg-cover bg-center mix-blend-overlay"></div>
            </div>
            <div className="relative z-10 flex flex-col md:flex-row justify-between items-center gap-6">
              <div>
                <h1 className="text-3xl font-bold text-white mb-2">Reportes Profesionales</h1>
                <p className="text-blue-100 max-w-xl">
                  Genera reportes ejecutivos para la toma de decisiones o técnicos para análisis detallado.
                </p>
              </div>

              {/* Quick Stats Cards */}
              <div className="grid grid-cols-2 gap-3 w-full md:w-auto">
                <div className="bg-white/10 backdrop-blur-md border border-white/20 rounded-xl p-4 text-white">
                  <div className="text-blue-200 text-xs mb-1">Total Ahorrado</div>
                  <div className="text-2xl font-bold">$352.200</div>
                </div>
                <div className="bg-white/10 backdrop-blur-md border border-white/20 rounded-xl p-4 text-white">
                  <div className="text-blue-200 text-xs mb-1">CO₂ Evitado</div>
                  <div className="text-2xl font-bold">1.29t</div>
                </div>
              </div>
            </div>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">

            {/* Generator Panel */}
            <div className="lg:col-span-1 space-y-6">
              <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6">
                <h2 className="text-lg font-bold text-gray-900 mb-6 flex items-center gap-2">
                  <div className="w-8 h-8 rounded-lg bg-blue-100 flex items-center justify-center text-blue-600">
                    <i className="ri-settings-4-line"></i>
                  </div>
                  Configuración
                </h2>

                <div className="space-y-5">
                  {/* Tipo de Reporte */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Tipo de Reporte</label>
                    <div className="grid grid-cols-1 gap-2">
                      {[
                        { id: 'executive', label: 'Ejecutivo', desc: 'Orientado al negocio', icon: 'ri-briefcase-line' },
                        { id: 'technical', label: 'Técnico', desc: 'Análisis detallado', icon: 'ri-line-chart-line' }
                      ].map((type) => (
                        <button
                          key={type.id}
                          onClick={() => setSelectedType(type.id)}
                          className={`px-4 py-3 rounded-xl text-sm font-medium transition-all border text-left ${selectedType === type.id
                            ? 'bg-blue-50 border-blue-200 text-blue-700 shadow-sm'
                            : 'bg-white border-gray-200 text-gray-600 hover:bg-gray-50'
                            }`}
                        >
                          <div className="flex items-center gap-3">
                            <i className={`${type.icon} text-lg`}></i>
                            <div>
                              <div className="font-bold">{type.label}</div>
                              <div className="text-xs opacity-70">{type.desc}</div>
                            </div>
                          </div>
                        </button>
                      ))}
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Período del Reporte</label>
                    <div className="grid grid-cols-2 gap-2">
                      {['week', 'month', 'quarter', 'year'].map((period) => (
                        <button
                          key={period}
                          onClick={() => setSelectedPeriod(period)}
                          className={`px-4 py-3 rounded-xl text-sm font-medium transition-all border ${selectedPeriod === period
                            ? 'bg-blue-50 border-blue-200 text-blue-700 shadow-sm'
                            : 'bg-white border-gray-200 text-gray-600 hover:bg-gray-50'
                            }`}
                        >
                          {period === 'week' && 'Semana'}
                          {period === 'month' && 'Mes'}
                          {period === 'quarter' && 'Trimestre'}
                          {period === 'year' && 'Año'}
                        </button>
                      ))}
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Formato de Salida</label>
                    <div className="grid grid-cols-2 gap-2">
                      {[
                        { id: 'pdf', label: 'PDF', icon: 'ri-file-pdf-line' },
                        { id: 'excel', label: 'Excel', icon: 'ri-file-excel-line' }
                      ].map((format) => (
                        <button
                          key={format.id}
                          onClick={() => setSelectedFormat(format.id)}
                          className={`px-3 py-3 rounded-xl text-sm font-medium transition-all border flex flex-col items-center justify-center gap-1 ${selectedFormat === format.id
                            ? 'bg-indigo-50 border-indigo-200 text-indigo-700 shadow-sm'
                            : 'bg-white border-gray-200 text-gray-600 hover:bg-gray-50'
                            }`}
                        >
                          <i className={`${format.icon} text-lg`}></i>
                          <span className="uppercase">{format.label}</span>
                        </button>
                      ))}
                    </div>
                  </div>

                  <div className="pt-4">
                    <Button
                      onClick={handleGenerateReport}
                      disabled={isGenerating}
                      variant="primary"
                      size="lg"
                      className="w-full justify-center shadow-lg shadow-blue-200"
                    >
                      {isGenerating ? (
                        <>
                          <i className="ri-loader-4-line animate-spin mr-2"></i>
                          Generando...
                        </>
                      ) : (
                        <>
                          <i className="ri-download-cloud-2-line mr-2"></i>
                          Generar y Descargar
                        </>
                      )}
                    </Button>
                  </div>
                </div>
              </div>

              <div className="bg-gradient-to-br from-indigo-500 to-purple-600 rounded-2xl shadow-lg p-6 text-white relative overflow-hidden">
                <div className="absolute top-0 right-0 -mt-4 -mr-4 w-24 h-24 bg-white/10 rounded-full blur-xl"></div>
                <div className="relative z-10">
                  <h3 className="font-bold text-lg mb-2">¿Necesitas ayuda?</h3>
                  <p className="text-indigo-100 text-sm mb-4">
                    Si necesitas un reporte personalizado o integración automática, contacta con soporte.
                  </p>
                  <button className="text-sm font-medium bg-white/20 hover:bg-white/30 transition-colors px-4 py-2 rounded-lg backdrop-blur-sm">
                    Contactar Soporte
                  </button>
                </div>
              </div>
            </div>

            {/* Content Area */}
            <div className="lg:col-span-2 space-y-8">

              {/* Recent Reports */}
              <div>
                <h2 className="text-lg font-bold text-gray-900 mb-4 flex items-center gap-2">
                  <i className="ri-history-line text-gray-400"></i>
                  Historial Reciente
                </h2>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {monthlyReports.map((report, index) => (
                    <div key={index} className="group bg-white rounded-2xl p-5 shadow-sm border border-gray-100 hover:shadow-md transition-all hover:-translate-y-1 cursor-pointer">
                      <div className="flex justify-between items-start mb-4">
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 rounded-full bg-green-50 flex items-center justify-center text-green-600 group-hover:bg-green-100 transition-colors">
                            <i className="ri-file-list-3-line text-lg"></i>
                          </div>
                          <div>
                            <h3 className="font-bold text-gray-900">{report.month}</h3>
                            <p className="text-xs text-gray-500">Reporte Mensual</p>
                          </div>
                        </div>
                        <button className="text-gray-400 hover:text-blue-600 transition-colors">
                          <i className="ri-download-2-line text-xl"></i>
                        </button>
                      </div>

                      <div className="grid grid-cols-2 gap-y-2 gap-x-4 text-sm">
                        <div className="flex justify-between">
                          <span className="text-gray-500">Producción</span>
                          <span className="font-medium text-gray-900">{report.totalProduction.toFixed(0)} kWh</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-gray-500">Ahorro</span>
                          <span className="font-medium text-green-600">${report.totalSavings.toLocaleString('es-CL')}</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-gray-500">Eficiencia</span>
                          <span className="font-medium text-blue-600">{report.avgEfficiency}%</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-gray-500">CO₂</span>
                          <span className="font-medium text-gray-900">{report.co2Avoided.toFixed(0)} kg</span>
                        </div>
                      </div>
                    </div>
                  ))}

                  <div className="bg-gray-50 rounded-2xl p-5 border border-dashed border-gray-300 flex flex-col items-center justify-center text-center hover:bg-gray-100 transition-colors cursor-pointer group">
                    <div className="w-12 h-12 rounded-full bg-white shadow-sm flex items-center justify-center text-gray-400 mb-3 group-hover:scale-110 transition-transform">
                      <i className="ri-add-line text-2xl"></i>
                    </div>
                    <p className="font-medium text-gray-900">Generar Nuevo</p>
                    <p className="text-xs text-gray-500 mt-1">Personalizar reporte</p>
                  </div>
                </div>
              </div>

              {/* Data Table */}
              <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden">
                <div className="p-6 border-b border-gray-100 flex justify-between items-center">
                  <h2 className="text-lg font-bold text-gray-900 flex items-center gap-2">
                    <i className="ri-table-line text-gray-400"></i>
                    Datos Detallados
                  </h2>
                  <button className="text-sm text-blue-600 font-medium hover:text-blue-700">
                    Ver todo
                  </button>
                </div>
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead>
                      <tr className="bg-gray-50/50 border-b border-gray-100">
                        <th className="text-left py-4 px-6 text-xs font-semibold text-gray-500 uppercase tracking-wider">Período</th>
                        <th className="text-right py-4 px-6 text-xs font-semibold text-gray-500 uppercase tracking-wider">Producción</th>
                        <th className="text-right py-4 px-6 text-xs font-semibold text-gray-500 uppercase tracking-wider">Consumo</th>
                        <th className="text-right py-4 px-6 text-xs font-semibold text-gray-500 uppercase tracking-wider">Autoconsumo</th>
                        <th className="text-right py-4 px-6 text-xs font-semibold text-gray-500 uppercase tracking-wider">Ahorro</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100">
                      {reportData.map((row, index) => (
                        <tr key={index} className="hover:bg-gray-50 transition-colors">
                          <td className="py-4 px-6 font-medium text-gray-900">{row.period}</td>
                          <td className="py-4 px-6 text-right text-gray-600">{row.production.toFixed(1)} <span className="text-xs text-gray-400">kWh</span></td>
                          <td className="py-4 px-6 text-right text-gray-600">{row.consumption.toFixed(1)} <span className="text-xs text-gray-400">kWh</span></td>
                          <td className="py-4 px-6 text-right">
                            <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                              {row.selfConsumption.toFixed(1)}%
                            </span>
                          </td>
                          <td className="py-4 px-6 text-right font-medium text-green-600">${row.savings.toLocaleString('es-CL', { maximumFractionDigits: 0 })}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
